/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Shared phased init for resource edit pages: seeding → hydrating → ready.
 * Template should bind hydrate-sensitive UI with
 * `<adn-promise-spinner [query]="editPhaseQuery" [andHide]="true">`
 * so pre-ready mutations never race the first CD turn (NG0100).
 */
import {signal, WritableSignal} from '@angular/core';

import {deferSignalStateWrite} from './template-state';

export type EditPagePhase = 'seeding' | 'hydrating' | 'ready';

export interface EditPagePhaseApi {
  readonly phase: WritableSignal<EditPagePhase>;
  readonly revision: WritableSignal<number>;
  /** Wait for a named async gate before entering ready (no-op once ready). */
  wait(gate: string): void;
  /** Clear a gate; enters ready when none remain. */
  done(gate: string): void;
  /** No async gates — schedule ready after seeding. */
  finishWithoutGates(): void;
  /**
   * Template-facing write. Sync while the form is still behind the phase
   * spinner; deferred once ready so later hydrates cannot NG0100.
   */
  viewWrite(fn: () => void): void;
  /** Awaitable viewWrite for promise chains that depend on the applied state. */
  viewTask<T>(fn: () => T | PromiseLike<T>): Promise<T>;
  isReady(): boolean;
}

export function attachEditPagePhase(host: any): EditPagePhaseApi {
  const phase = signal<EditPagePhase>('seeding');
  const resolved = signal(false);
  const revision = signal(0);
  host.editPhase = phase;
  host.editPhaseRevision = revision;
  host.editPhaseQuery = {
    get $resolved(): boolean {
      return resolved();
    }
  };

  const gates = new Map<string, number>();
  let readyScheduled = false;

  function enterReady(): void {
    if (phase() === 'ready' || readyScheduled) {
      return;
    }
    if (gates.size > 0) {
      return;
    }
    readyScheduled = true;
    const commit = () => {
      readyScheduled = false;
      if (phase() === 'ready' || gates.size > 0) {
        return;
      }
      phase.set('ready');
      resolved.set(true);
      if (typeof host.scheduleResumeFormDirtyTrackingAfterInit === 'function') {
        host.scheduleResumeFormDirtyTrackingAfterInit();
      } else if (typeof host.resumeFormDirtyTracking === 'function') {
        host.resumeFormDirtyTracking();
      }
    };
    deferSignalStateWrite(commit);
  }

  function beginHydrating(): void {
    if (phase() === 'seeding') {
      phase.set('hydrating');
    }
  }

  const api: EditPagePhaseApi = {
    phase,
    revision,
    wait(gate: string): void {
      if (phase() === 'ready') {
        return;
      }
      gates.set(gate, (gates.get(gate) || 0) + 1);
      beginHydrating();
    },
    done(gate: string): void {
      const count = gates.get(gate) || 0;
      if (count > 1) {
        gates.set(gate, count - 1);
      } else {
        gates.delete(gate);
      }
      enterReady();
    },
    finishWithoutGates(): void {
      enterReady();
    },
    viewWrite(fn: () => void): void {
      if (phase() === 'ready') {
        deferSignalStateWrite(() => {
          fn();
          revision.update((value) => value + 1);
        });
        return;
      }
      beginHydrating();
      fn();
    },
    viewTask<T>(fn: () => T | PromiseLike<T>): Promise<T> {
      if (phase() !== 'ready') {
        beginHydrating();
        try {
          return Promise.resolve(fn());
        } catch (error) {
          return Promise.reject(error);
        }
      }
      return new Promise<T>((resolve, reject) => {
        api.viewWrite(() => {
          try {
            resolve(fn());
          } catch (error) {
            reject(error);
          }
        });
      });
    },
    isReady(): boolean {
      return phase() === 'ready';
    }
  };

  host.editPhaseApi = api;
  return api;
}
